import { CoreButton, CoreModalDialogFooter, CoreTabs } from '@components'
import { toastSuccess } from '@utils'
import {
  paths,
  useCreateExpenseMutation,
  useCurrencies,
  useGetExpenseAttachment,
  useGetReportQuery,
  useUpdateExpenseMutation
} from 'mmfintech-backend-api'
import type { ExpenseRequest, ExpenseResponse } from 'mmfintech-backend-api/types'
import { fixDateOnly, tr, useFormValues } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ExpenseAdvancedTab from './ExpenseAdvancedTab'
import ExpenseDetailsTab from './ExpenseDetailsTab'
import ExpenseRecipientTab from './ExpenseRecipientTab'

import './addExpenseModal.scss'

interface AddExpenseModalProps {
  reportId?: number
  expense?: ExpenseResponse
  rawFileInfo?: File
  onClose?: () => void
  reload?: () => void
}

const initialValues = {
  id: {},

  file: { required: true, step: 1 },
  reportId: { required: true, step: 1 },
  currency: { required: true, step: 1 },
  amount: { required: true, step: 1 },
  reimbursement: { required: true, step: 1, value: false },
  dateOfPayment: { required: true, step: 1 },
  dueDate: { step: 1 },
  invoiceNumber: { validation: 'safe-string', maxLength: 30, step: 1 },
  category: { validation: 'safe-string', maxLength: 60, step: 1 },
  accountingCategory: { step: 1 },

  counterpartyId: { step: 2 },
  recipientId: { step: 2 },
  recipientName: { step: 2 },

  description: { validation: 'safe-string', maxLength: 500, step: 3 },
  type: { validation: 'safe-string', maxLength: 60, step: 3 },
  subCategory: { validation: 'safe-string', maxLength: 60, step: 3 },
  businessUnit: { validation: 'safe-string', maxLength: 60, step: 3 },
  paymentType: { validation: 'safe-string', maxLength: 60, step: 3 },
  vendorName: { validation: 'safe-string', maxLength: 60, step: 3 }
}

const AddExpenseModal = (props: AddExpenseModalProps) => {
  const { reportId, expense, rawFileInfo, onClose, reload } = props

  const params = useParams<{ reportId: string }>()

  const tabs = [
    { value: 'EXPENSE', label: tr('FRONTEND.EXPENSE_MODAL.TABS.EXPENSE', 'Expense') },
    { value: 'RECIPIENT', label: tr('FRONTEND.EXPENSE_MODAL.TABS.RECIPIENT', 'Recipient') },
    { value: 'ADVANCED', label: tr('FRONTEND.EXPENSE_MODAL.TABS.ADVANCED', 'Advanced') }
  ]

  const [activeTab, setActiveTab] = useState('EXPENSE')
  const [uploadedFile, setUploadedFile] = useState(null)

  const [error, setError] = useState(null)

  const history = useHistory()

  const { getExpenseAttachment } = useGetExpenseAttachment()

  const { currencyOptionsSimple } = useCurrencies()

  const currentReportId = useMemo(
    () => params.reportId || reportId || expense?.reportId,
    [params.reportId, reportId, expense?.reportId]
  )

  const { data: currentReport } = useGetReportQuery({ reportId: currentReportId }, { skip: !currentReportId })

  const [createExpenseRequest, { error: createError, isLoading: createLoading, reset: resetCreate }] =
    useCreateExpenseMutation()
  const [updateExpenseRequest, { error: updateError, isLoading: updateLoading, reset: resetUpdate }] =
    useUpdateExpenseMutation()

  const formValues = useFormValues({ ...initialValues })

  const handleTabChange = (tab: string) => {
    if (tab === 'RECIPIENT' && !formValues.isValid('currency')) {
      setActiveTab('EXPENSE')
    } else {
      setActiveTab(tab)
    }
  }

  const saveExpense = async (onSuccess: () => void) => {
    setError(null)
    if (formValues.areValid()) {
      const { id: expenseId, reportId: internalReportId, ...preparedValues } = formValues.prepare()
      preparedValues.counterpartyId = preparedValues.counterpartyId?.value || preparedValues.counterpartyId
      preparedValues.dateOfPayment = fixDateOnly(preparedValues.dateOfPayment)
      preparedValues.dueDate = fixDateOnly(preparedValues?.dueDate)
      const reportId = internalReportId?.value || internalReportId

      try {
        if (expenseId) {
          await updateExpenseRequest({
            ...(preparedValues as ExpenseRequest),
            reportId,
            expenseId
          }).unwrap()
          toastSuccess(tr('FRONTEND.EXPENSE_MODAL.EDITED', 'Expense successfully updated'))
        } else {
          await createExpenseRequest({ ...(preparedValues as ExpenseRequest), reportId }).unwrap()
          toastSuccess(tr('FRONTEND.EXPENSE_MODAL.CREATED', 'Expense successfully created'))
        }

        typeof reload === 'function' && reload()
        onSuccess()
      } catch (error) {
        formValues.handleErrors(error)
      }
    } else {
      setError(tr('FRONTEND.EXPENSE_MODAL.ERRORS.REQUIRED_FIELDS', 'Please fill in all required fields'))

      const isStep1Valid = formValues.areValid(1)
      const isStep2Valid = formValues.areValid(2)
      const isStep3Valid = formValues.areValid(3)

      if (!isStep1Valid) {
        setActiveTab('EXPENSE')
      } else if (!isStep2Valid) {
        setActiveTab('RECIPIENT')
      } else if (!isStep3Valid) {
        setActiveTab('ADVANCED')
      }
    }
  }

  const handleSaveClick = () => {
    void saveExpense(() => {
      typeof onClose === 'function' && onClose()
      history.push(paths.expenseManagement.reportDetails(formValues.getValue('reportId')))
    })
  }

  const handleSaveAndNewClick = () => {
    void saveExpense(() => {
      formValues.cleanup(['reportId', 'reimbursement'], true)
      handleRemoveFile()
      setError(null)
    })
  }

  const handleSelectFile = useCallback(
    (file: File) => {
      setUploadedFile({
        fileUrl: URL.createObjectURL(file),
        fileName: file.name,
        type: file.type
      })
      formValues.setValue('file', file)
    },
    [formValues]
  )

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null)
    formValues.setValue('file', null)
  }, [formValues])

  const resetState = useCallback(() => {
    setActiveTab('EXPENSE')
    setUploadedFile(null)
    formValues.reset({ ...initialValues })
    resetCreate()
    resetUpdate()
  }, [formValues, resetCreate, resetUpdate])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on unmount
  useEffect(() => {
    return () => {
      resetState()
    }
  }, [])

  useEffect(() => {
    if (expense?.id) {
      formValues.setup({
        id: expense.id,

        reportId: expense.reportId,
        currency: expense.currency,
        amount: expense.amount,
        reimbursement: Boolean(expense.reimbursement),
        dateOfPayment: expense.dateOfPayment,
        dueDate: expense.dueDate,
        invoiceNumber: expense.invoiceNumber,
        category: expense.category,
        accountingCategory: expense.accountingCategory,

        counterpartyId: expense.counterpartyId,
        recipientId: expense.recipientId,
        recipientName: expense.recipientName,

        description: expense.description,
        type: expense.type,
        subCategory: expense.subCategory,
        businessUnit: expense.businessUnit,
        paymentType: expense.paymentType,
        vendorName: expense.vendorName
      })

      if (expense.reportId) {
        getExpenseAttachment(expense.reportId, expense.id, (data: any, filename: string, contentType: string) => {
          const file = new File([data], filename || `expense-${expense.id}-attachment.pdf`, { type: contentType })
          setUploadedFile({
            fileUrl: URL.createObjectURL(file),
            fileName: file.name,
            type: file.type
          })
          formValues.setValue('file', file)
        })
      }
    }
  }, [expense?.id])

  useEffect(() => {
    if (currentReport?.id) {
      formValues.setValue('reportId', currentReport?.id)
    }
  }, [currentReport?.id])

  useEffect(() => {
    if (rawFileInfo) {
      handleSelectFile(rawFileInfo)
    }
  }, [rawFileInfo])

  return (
    <div className='expense-modal-wrapper'>
      <div className='expense-modal-navigation'>
        <CoreTabs tabs={tabs} onChangeTab={handleTabChange} tabState={activeTab} />
      </div>

      <div className='expense-modal-container'>
        {activeTab === 'EXPENSE' && (
          <ExpenseDetailsTab
            formValues={formValues}
            fileObject={uploadedFile}
            setFileObject={setUploadedFile}
            reportDisabled={Boolean(currentReport)}
            currencyOptions={currencyOptionsSimple}
          />
        )}
        {activeTab === 'RECIPIENT' && <ExpenseRecipientTab formValues={formValues} />}
        {activeTab === 'ADVANCED' && <ExpenseAdvancedTab formValues={formValues} />}

        <CoreModalDialogFooter>
          <ErrorDisplay error={[createError, updateError, error]} />

          <div className='expense-modal-footer'>
            {!expense?.id && (
              <CoreButton
                isLoading={createLoading || updateLoading}
                text={tr('FRONTEND.EXPENSE_MODAL.BUTTON_SAVE_AND_NEW', 'Save & New')}
                data-test='save-edit-new-expense'
                onClick={handleSaveAndNewClick}
                size='large'
                variation='tertiary'
              />
            )}
            <CoreButton
              isLoading={createLoading || updateLoading}
              text={tr('FRONTEND.EXPENSE_MODAL.BUTTON_SAVE', 'Save & Close')}
              data-test='save-edit-expense'
              onClick={handleSaveClick}
              size='large'
              fullWidth
            />
          </div>
        </CoreModalDialogFooter>
      </div>
    </div>
  )
}

export default AddExpenseModal
